import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Route as RouteIcon, MapPin, Clock } from 'lucide-react';
import RotaMap from '@/features/rotas/RotaMap';
import { buscarRota, gerarRota, buscarConfig } from '@/features/pedidos/api';
import { usePedidosStore, dataHojeISO } from '@/features/pedidos/store';
import type { ConfigApp, RotaPayload } from '@/features/pedidos/types';
import Button from '@/components/Button';
import Pill from '@/components/Pill';
import Numeric from '@/components/Numeric';
import { useToast } from '@/components/Toast';
import { formatKm, formatDuracao, formatTicket, googleMapsRouteUrl } from '@/lib/format';
import { cn } from '@/lib/cn';

export default function RotasPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [rota, setRota] = useState<RotaPayload | null>(null);
  const [config, setConfig] = useState<ConfigApp | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [gerando, setGerando] = useState(false);
  const [selecionado, setSelecionado] = useState<number | null>(null);
  const [aberto, setAberto] = useState(false);
  const dataAtual = usePedidosStore((s) => s.data);
  const dataAlvo = dataAtual === 'all' ? dataHojeISO() : dataAtual;

  const gMapsUrl = useMemo(
    () => (rota ? googleMapsRouteUrl(rota.ponto_partida, rota.rota_otimizada) : null),
    [rota],
  );

  // estável para não re-init RotaMap a cada render
  const onSelectMarker = useCallback((id: number) => setSelecionado(id), []);

  // entrada cinematográfica (sempre roda 1x)
  useEffect(() => {
    const t = window.setTimeout(() => setAberto(true), 30);
    return () => window.clearTimeout(t);
  }, []);

  // fetch inicial — re-roda se data muda (ex: usuário alterou data na Kanban)
  useEffect(() => {
    let alive = true;
    (async () => {
      setCarregando(true);
      try {
        const [r, c] = await Promise.all([buscarRota(dataAlvo), buscarConfig()]);
        if (!alive) return;
        setRota(r);
        setConfig(c);
      } catch (err) {
        if (alive) toast('error', `Falha ao carregar rota — ${(err as Error).message}.`);
      } finally {
        if (alive) setCarregando(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [dataAlvo, toast]);

  // re-busca a rota quando o backend sinaliza recálculo/invalidação via WS
  // (cron noturno, pedido excluído em outra aba, etc).
  useEffect(() => {
    let alive = true;
    const onRotaMudou = () => {
      buscarRota(dataAlvo)
        .then((r) => {
          if (alive) setRota(r);
        })
        .catch(() => {
          /* erro silencioso — fetch principal já reporta falhas */
        });
    };
    window.addEventListener('rota:mudou', onRotaMudou);
    return () => {
      alive = false;
      window.removeEventListener('rota:mudou', onRotaMudou);
    };
  }, [dataAlvo]);

  async function onGerar() {
    setGerando(true);
    try {
      const resp = await gerarRota(dataAlvo);
      if (!resp.rota) {
        toast('info', resp.aviso || 'Nenhum pedido elegível para a rota.');
        if (resp.diagnostico?.enderecos_falha?.length) {
          const falhas = resp.diagnostico.enderecos_falha
            .map((f) => f.numero_ticket)
            .join(', ');
          toast('error', `Sem coords: ${falhas}. Edite o endereço.`);
        }
      } else {
        setRota(resp.rota);
        usePedidosStore.getState().carregar();
        toast('success', `Rota traçada — ${resp.rota.rota_otimizada.length} paradas, ${formatKm(resp.rota.distancia_total_m)}.`);
      }
    } catch (err) {
      toast('error', `Falha ao gerar rota — ${(err as Error).message}.`);
    } finally {
      setGerando(false);
    }
  }

  function onVoltar() {
    setAberto(false);
    window.setTimeout(() => navigate('/'), 360);
  }

  return (
    <div className="fixed inset-0 z-30 bg-void overflow-hidden">
      {/* scrim entrada */}
      <div
        className={cn(
          'absolute inset-0 bg-void transition-opacity duration-slow ease-out-expo',
          aberto ? 'opacity-0' : 'opacity-100',
        )}
      />

      {/* shell cinematográfico */}
      <div
        className={cn(
          'absolute inset-0 transition-transform duration-cinema ease-out-expo',
          aberto ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-[2vh] scale-[0.99] opacity-0',
        )}
      >
        <div className="grid h-full grid-cols-[minmax(0,1fr)_440px]">
          {/* mapa */}
          <div className="relative">
            <RotaMap rota={rota} selecionado={selecionado} onSelect={onSelectMarker} />
            <header className="absolute top-s7 left-s7 right-s7 z-[1000] flex items-center justify-between pointer-events-none">
              <div className="flex items-center gap-s4 pointer-events-auto">
                <button
                  onClick={onVoltar}
                  className="inline-flex items-center gap-s3 h-[40px] px-s5 rounded-md bg-elev/80 border border-[var(--border-strong)] text-fg-1 backdrop-blur-md hover:bg-card transition-colors duration-fast"
                  style={{ boxShadow: '0 10px 30px rgba(0,0,0,0.55)' }}
                >
                  <ArrowLeft size={16} strokeWidth={1.5} /> Voltar
                </button>
                <div className="flex flex-col">
                  <span className="t-overline">Rota otimizada</span>
                  <span className="font-display italic text-[28px] leading-tight text-fg-1 tracking-[-0.02em]">
                    {rota
                      ? `${rota.rota_otimizada.length} paradas em ${formatDuracao(rota.duracao_total_s)}`
                      : 'Nada para entregar hoje.'}
                  </span>
                </div>
              </div>
            </header>
          </div>

          {/* timeline lateral */}
          <aside className="relative bg-panel border-l border-[var(--border-subtle)] flex flex-col">
            <header className="px-s7 py-s5 border-b border-[var(--border-subtle)]">
              <div className="flex items-center justify-between gap-s3">
                <span className="t-overline">Paradas</span>
                <div className="flex items-center gap-s2">
                  {gMapsUrl && (
                    <a
                      href={gMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-s2 h-[28px] px-s3 rounded-sm border border-[var(--border)] bg-card text-fg-2 text-[12px] font-semibold hover:bg-card-hover hover:text-fg-1 hover:border-[var(--border-strong)] transition-all duration-fast"
                      title="Abrir rota no Google Maps"
                    >
                      <ExternalLink size={12} strokeWidth={1.6} />
                      Google Maps
                    </a>
                  )}
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={onGerar}
                    disabled={gerando}
                    leading={
                      <RouteIcon size={12} strokeWidth={1.6} className={gerando ? 'animate-spin' : ''} />
                    }
                  >
                    {gerando ? 'Otimizando…' : 'Gerar agora'}
                  </Button>
                </div>
              </div>
              {rota && (
                <div className="flex items-center gap-s5 mt-s4">
                  <Stat label="Distância" valor={formatKm(rota.distancia_total_m)} />
                  <Stat label="Tempo" valor={formatDuracao(rota.duracao_total_s)} />
                  <Stat label="Paradas" valor={String(rota.rota_otimizada.length)} mono />
                </div>
              )}
            </header>

            <div className="flex-1 overflow-y-auto px-s7 py-s5">
              {!rota && !carregando && (
                <p className="font-display italic text-[18px] text-fg-3 mt-s8 text-center">
                  Nada para entregar hoje.
                </p>
              )}
              {rota && (
                <ol className="flex flex-col gap-s4 relative">
                  <ParadaPartida partida={rota.ponto_partida} />
                  <span
                    className="absolute left-[10px] top-[24px] bottom-[24px] w-[1px] bg-[var(--border)]"
                    aria-hidden
                  />
                  {rota.rota_otimizada.map((p, idx) => (
                    <li
                      key={p.pedido_id}
                      onClick={() => setSelecionado(p.pedido_id)}
                      className={cn(
                        'relative pl-s7 pr-s4 py-s3 -mx-s3 rounded-md cursor-pointer transition-colors duration-fast',
                        selecionado === p.pedido_id
                          ? 'bg-card border border-[var(--border-strong)]'
                          : 'hover:bg-card border border-transparent',
                      )}
                    >
                      <span
                        className={cn(
                          'absolute left-[5px] top-[14px] w-[12px] h-[12px] rounded-full bg-canvas border',
                          idx === rota.rota_otimizada.length - 1
                            ? 'border-petal-500'
                            : 'border-moss-300',
                        )}
                        aria-hidden
                      />
                      <div className="flex items-baseline justify-between gap-s3">
                        <span className="font-mono text-[11px] text-moss-300">
                          {formatTicket(p.numero_ticket)}
                        </span>
                        <span className="font-mono text-[10px] text-fg-3">#{p.ordem}</span>
                      </div>
                      <div className="font-body font-semibold text-[13.5px] text-fg-1 mt-s1">
                        {p.nome_cliente}
                      </div>
                      <div className="flex gap-s2 items-start mt-s2 font-body text-[11.5px] text-fg-3 leading-snug">
                        <MapPin size={11} strokeWidth={1.6} className="mt-[2px] shrink-0 opacity-70" />
                        <span className="line-clamp-2">{p.endereco_entrega}</span>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            {config && (
              <footer className="px-s7 py-s4 border-t border-[var(--border-subtle)] flex items-center justify-between">
                <span className="font-body text-[11px] text-fg-3">
                  Corte às <span className="font-mono text-fg-2">{config.horario_corte}</span>
                </span>
                <Pill tone="moss" dot={false}>
                  <Clock size={10} strokeWidth={1.8} />
                  Cron {config.cron_rota}
                </Pill>
              </footer>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, valor, mono }: { label: string; valor: string; mono?: boolean }) {
  return (
    <div className="flex flex-col">
      <span className="t-overline">{label}</span>
      {mono ? (
        <Numeric size="md">{valor}</Numeric>
      ) : (
        <span className="font-mono text-[14px] text-fg-1 tnum mt-[3px]">{valor}</span>
      )}
    </div>
  );
}

function ParadaPartida({ partida }: { partida: { endereco: string; lat: number; lng: number } }) {
  return (
    <li className="relative pl-s7 pr-s4 py-s3 -mx-s3 rounded-md">
      <span
        className="absolute left-[5px] top-[14px] w-[12px] h-[12px] rounded-full bg-canvas border border-amber-500"
        aria-hidden
      />
      <span className="font-mono text-[11px] text-amber-500">PARTIDA</span>
      <div className="flex gap-s2 items-start mt-s1 font-body text-[12.5px] text-fg-1 leading-snug">
        <MapPin size={12} strokeWidth={1.6} className="mt-[2px] shrink-0 opacity-70" />
        <span>{partida.endereco}</span>
      </div>
    </li>
  );
}
